import { useTranslation } from 'react-i18next';

import { useQuizStore } from './store';
import type { BoardDocument } from '../filmstrip/documents';
import { EmptyState } from '../../components';
import { ChartBarIcon, CloseLineIcon, DockSideIcon, MoveIcon } from '../../layout/icons';
import { useDraggableChrome } from '../../layout/useDraggableChrome';
import '../../styles/rail-drag.css';

export interface QuizResultsPanelProps {
  /** The open quiz document. */
  document: BoardDocument;
}

// A generated set is painted on the board, never assigned to students, so no attempt exists
// anywhere to report. The panel says so instead of inventing a class.
// Floating by default: docking it narrows the board and the quiz card shrinks with it.
export function QuizResultsPanel({ document: item }: QuizResultsPanelProps) {
  const { t } = useTranslation('quiz');
  const closeResults = useQuizStore((state) => state.closeResults);
  const docked = useQuizStore((state) => state.resultsDocked);
  const toggleDock = useQuizStore((state) => state.toggleResultsDock);
  // only the floating card moves; the docked column is anchored by its own CSS, and keeps the spot
  const drag = useDraggableChrome({ id: 'quiz-results', enabled: !docked });

  const className = [
    docked ? 'quiz-results quiz-results--docked' : 'quiz-results quiz-results--floating',
    drag.dragging ? 'quiz-results--dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <aside ref={drag.ref} className={className} aria-label={t('resultsPanel.label')}>
      <header className="quiz-results__header">
        {/* hidden by quiz.css when docked */}
        <button
          type="button"
          className="quiz-results__handle"
          aria-label={t('resultsPanel.drag')}
          {...drag.handleProps}
        >
          <MoveIcon size={18} />
        </button>
        <div className="quiz-results__heading">
          <div className="quiz-results__title">{t('resultsPanel.title')}</div>
          <div className="quiz-results__subtitle">{item.title}</div>
        </div>
        {/* docking is opt-in: it narrows the board, which shrinks the quiz card */}
        <button
          type="button"
          className="quiz-results__dock"
          aria-pressed={docked}
          aria-label={docked ? t('resultsPanel.undock') : t('resultsPanel.dock')}
          onClick={toggleDock}
        >
          <DockSideIcon size={18} />
        </button>
        {/* the stroked close, not the filled house glyph: it sits beside a base() glyph and has to
            carry the same stroke weight, or the pair reads as two different control vocabularies */}
        <button type="button" className="quiz-results__close" aria-label={t('resultsPanel.close')} onClick={closeResults}>
          <CloseLineIcon size={18} />
        </button>
      </header>

      <div className="quiz-results__scroll">
        <EmptyState
          icon={<ChartBarIcon size={26} />}
          title={t('resultsPanel.emptyTitle')}
          description={t('resultsPanel.emptyBody')}
          compact={true}
        />
      </div>
    </aside>
  );
}
